import rfdc from "rfdc";
import * as sqrl from "squirrelly";
import type { FetcherFunction } from "squirrelly/dist/types/config";
import type {
  FilterFunction,
  HelperFunction,
} from "squirrelly/dist/types/containers";
import type { TranslationFunction } from "../types/common";
import type { IDataMap } from "../types/data-source";

const deepclone = rfdc();

interface IStringTemplateHelperArgs {
  mode?: "EDIT" | "VIEW";
  t?: TranslationFunction;
}

export const doubleBracesStringRegex = /{{(?:\s|&nbsp;)*(.*?)(?:\s|&nbsp;)*}}/g;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null) {
    return false;
  }
  if (typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  return Object.getPrototypeOf(value) === Object.prototype;
};

/**
 * 점(`.`)으로 분리된 경로를 순회하며 비어 있는 위치에만 기본값을 채워 넣는다.
 *
 * 경로를 따라가면서 중간 객체나 배열이 없으면 생성하고, 이미 값이 정의돼 있으면 덮어쓰지 않는다.
 *
 * 예시:
 * ```ts
 * const target: Record<string, unknown> = {};
 *
 * setNestedValueIfMissing(target, ["user", "name"], "홍길동");
 * // target === { user: { name: "홍길동" } }
 *
 * setNestedValueIfMissing(target, ["user", "name"], "성춘향");
 * // 기존 값이 유지되어 target.user.name === "홍길동"
 *
 * setNestedValueIfMissing(target, ["user", "profile", "age"], 29);
 * // target === { user: { name: "홍길동", profile: { age: 29 } } }
 * ```
 */
const setNestedValueIfMissing = (
  target: Record<string, unknown>,
  path: string[],
  value: unknown
) => {
  if (path.length === 0) {
    return;
  }

  let current: Record<string, unknown> = target;

  for (let index = 0; index < path.length; index += 1) {
    const segment = path[index]!;
    const isLast = index === path.length - 1;

    if (isLast) {
      if (
        !Object.prototype.hasOwnProperty.call(current, segment) ||
        current[segment] === undefined
      ) {
        current[segment] = value;
      }
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(current, segment)) {
      current[segment] = {};
    }

    const next = current[segment];
    if (!isPlainObject(next)) {
      return;
    }

    current = next as Record<string, unknown>;
  }
};

/**
 * 중첩된 점(`.`) 표기법을 사용한 키를 실제 중첩된 객체에 추가해줍니다. 기존 값은 덮어쓰지 않습니다.
 * 예: { "user.name": "홍길동" } → { "user.name": "홍길동", user: { name: "홍길동" } }
 */
export const normalizeDottedKeys = <T>(source: T): T => {
  if (!isPlainObject(source)) {
    return source;
  }

  const entries = Object.entries(source as Record<string, unknown>);
  const result: Record<string, unknown> = {};

  // 중첩된 하위 키를 먼저 처리
  entries.forEach(([key, value]) => {
    result[key] = normalizeDottedKeys(value);
  });

  // 점(.)으로 분리된 키를 찾아서 중첩된 객체로 변환
  entries.forEach(([key]) => {
    if (!key.includes(".")) {
      return;
    }

    const segments = key.split(".").filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    setNestedValueIfMissing(result, segments, result[key]);
  });

  return result as T;
};

/**
 * Squirrelly 의 filters 를 직접 커스터마이징하지 않고 새로운 변수로 만들어서 사용하기 위한 중간 객체
 * 기본적으로 제공되는 함수와 거의 동일하고, filters.define 하는 부분만 확장하여 사용
 */
const fetcher: FetcherFunction = (container, name) => {
  const filters = deepclone(sqrl.filters);

  Object.keys(Object.getPrototypeOf(sqrl.filters))
    .filter(
      (key) =>
        typeof sqrl.filters[key as keyof typeof sqrl.filters] === "function"
    )
    .forEach((key) =>
      Object.assign(filters, {
        [key]: sqrl.filters[key as keyof typeof sqrl.filters].bind(filters),
      })
    );

  filters.define("default", (str: string, defaultStr: string) => {
    if (str) {
      return str;
    }
    return defaultStr;
  });

  let result: FilterFunction | HelperFunction;
  switch (container) {
    case "H":
      result = sqrl.helpers.get(name);
      break;
    case "F":
      result = filters.get(name);
      break;
  }
  if (!result) {
    throw new Error(
      `Can't find ${container === "H" ? "helper" : "filter"} '${name}'`
    );
  }
  return result;
};

const renderDataMapWithSquirrelly = ({
  content,
  dataMap,
  undefinedHandling = "empty",
  errorHandling = "empty",
  t,
}: {
  content: string;
  dataMap: IDataMap;
  undefinedHandling?: "empty" | "original";
  errorHandling?: "empty" | "throw" | "original";
  t?: TranslationFunction;
}) => {
  const convertedDataMap: { [key: string]: unknown } = {
    user: {},
    data: {},
  };

  Object.entries(dataMap).forEach(([key, value]) => {
    if (!key) {
      return;
    }

    if (isPlainObject(value.data)) {
      convertedDataMap[key] = normalizeDottedKeys(value.data);
      return;
    }

    convertedDataMap[key] = value.data;
  });

  return content.replace(doubleBracesStringRegex, (subStr) => {
    // {{ translation.xxx }} 문법을 사용한 경우
    const match = subStr.match(/{{\s*translation\.(.*?)\s*}}/);
    if (t && match) {
      const translationKey = match[1] ?? "";
      return t(translationKey);
    }

    try {
      const result = sqrl.render(subStr, convertedDataMap, {
        useWith: true,
        l: fetcher,
        autoEscape: false,
      });

      if (result === "undefined") {
        switch (undefinedHandling) {
          case "empty":
            return "";
          case "original":
            return subStr;
        }
      }

      return result;
    } catch (e) {
      switch (errorHandling) {
        case "empty":
          return "";
        case "throw":
          throw e;
        case "original":
          return subStr;
      }
    }
  });
};

const parsePlaceholder = (
  innerMatch: string
): { path: string[]; defaultValue?: string } => {
  const cleaned = innerMatch.replace(/&nbsp;/g, " ").trim();

  const defaultMatch = cleaned.match(
    /\|\s*default\s*\(\s*(['"])(.*?)\1\s*\)\s*$/
  );

  const expression = defaultMatch
    ? cleaned.slice(0, defaultMatch.index ?? cleaned.length).trim()
    : cleaned;

  if (!expression || expression.endsWith(".") || expression.includes("..")) {
    throw new Error("Bad template syntax");
  }

  return { path: expression.split("."), defaultValue: defaultMatch?.[2] };
};

/**
 * 템플릿 문자열을 주어진 데이터로 렌더링합니다.
 * @param data 템플릿에 치환할 데이터 객체
 * @param path 치환할 값의 경로 (예: ["user", "name"])
 */
const resolveValue = (data: unknown, path: string[]): string | undefined => {
  if (data === undefined || data === null) {
    return undefined;
  }
  const [firstKey, ...restKeys] = path;
  if (path.length === 0 || !firstKey) {
    return String(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return resolveValue((data as any)[firstKey], restKeys);
};

type RenderTemplateArgs = {
  /** 치환 문자가 포함된 템플릿 문자열 */
  template: string;
  /** 템플릿에 치환할 데이터 객체 */
  data: object;
  /** 치환 중 오류가 발생했을 때의 처리 방식 (기본값: "original") */
  errorHandling?: "empty" | "throw" | "original";
  /** 치환할 값이 비어 있을 때의 처리 방식 (기본값: "empty") */
  emptyHandling?: "empty" | "throw" | "original";
};

/** squirrelly 안 쓴 버전 (직접 구현) */
export const renderTemplate2 = ({
  template,
  data,
  errorHandling = "original",
  emptyHandling = "empty",
}: RenderTemplateArgs): string => {
  const normalizedData = normalizeDottedKeys(data);

  return template.replace(
    doubleBracesStringRegex,
    (fullMatch: string, innerMatch: string) => {
      let resolvedValue: string | undefined = undefined;

      try {
        const { path, defaultValue } = parsePlaceholder(innerMatch);
        resolvedValue = resolveValue(normalizedData, path);
        if (!resolvedValue && defaultValue) {
          resolvedValue = defaultValue;
        }
      } catch (error: unknown) {
        switch (errorHandling) {
          case "empty":
            resolvedValue = "";
            break;
          case "throw":
            throw error;
          case "original":
          default:
            resolvedValue = fullMatch;
        }
      }

      if (
        resolvedValue === undefined ||
        resolvedValue === null ||
        resolvedValue === ""
      ) {
        switch (emptyHandling) {
          case "empty":
            return "";
          case "throw":
            throw new Error(`'${innerMatch}' is not defined`);
          case "original":
          default:
            return fullMatch;
        }
      }

      return resolvedValue;
    }
  );
};

export const renderTemplate = renderTemplate2;

export const stringTemplateHelper = ({
  mode = "VIEW",
  t,
}: IStringTemplateHelperArgs) => {
  return {
    /**
     * 문자열을 기본적인 형태로 렌더링합니다. 동작이 다른 부분은 `renderTextContent` 등 함수를 별도로 만들어 세분화합니다.
     */
    render(content: string, dataMap: IDataMap) {
      switch (mode) {
        case "EDIT":
          return content;
        case "VIEW":
          return renderDataMapWithSquirrelly({ content, dataMap, t });
      }
    },

    /**
     * 텍스트 노드의 콘텐츠를 렌더링합니다.
     */
    renderTextContent(content: string, dataMap: IDataMap) {
      switch (mode) {
        case "EDIT":
          return renderDataMapWithSquirrelly({
            content,
            dataMap,
            errorHandling: "original",
            undefinedHandling: "original",
          });
        case "VIEW":
          return renderDataMapWithSquirrelly({ content, dataMap, t });
      }
    },
  };
};

export const getCurrencyName = (currency: string) => {
  switch (currency) {
    case "KRW":
      return "원";
    case "USD":
      return "dollars";
    case "JPY":
      return "円";
    default:
      return currency;
  }
};

export const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case "KRW":
      return "₩";
    case "USD":
      return "$";
    case "JPY":
      return "¥";
    default:
      return currency;
  }
};

export const getDefaultFormat = (currency: string) => {
  switch (currency) {
    case "KRW":
      return "{{ price }}{{ currency_name }}";
    case "USD":
      return "{{ currency_symbol }}{{ price }}";
    case "JPY":
      return "{{ currency_symbol }}{{ price }}";
    default:
      return "{{ price }}{{ currency_name }}";
  }
};

export const renderProductItemPrice = (args: {
  format?: string | null;
  currency?: string | null;
  price?: number | null;
}) => {
  const currency = args.currency ?? "KRW";
  const format = args.format ?? getDefaultFormat(currency);
  const price = args.price ?? 0;

  return renderTemplate({
    template: format,
    data: {
      price: price.toLocaleString("ko"),
      currency_name: getCurrencyName(currency),
      currency_symbol: getCurrencySymbol(currency),
    },
  });
};
